/**
 * Kit Builder: gerencia contadores, limites, progresso e add-to-cart
 * para o bloco jv-kit-builder (kit personalizado com line item properties).
 *
 * Limite por item = min(block.max_quantity, estoque da variante na loja)
 * O produto Kit é adicionado ao carrinho com properties descrevendo os pratos.
 */
(function () {
  const getEffectiveMax = (el) => {
    const blockMax = parseInt(el.dataset.maxQty, 10) || 999;
    const storeQty = parseInt(el.dataset.storeQty, 10);
    let effective = isNaN(storeQty) ? blockMax : Math.min(blockMax, storeQty);

    if (window.JVRegionalStock && el.dataset.variantId) {
      const regionalData = (() => {
        try {
          return JSON.parse(el.dataset.regionalStock || '0');
        } catch (_) {
          return 0;
        }
      })();

      // Novo formato: inteiro unico no metacampo de produto.
      if (typeof regionalData === 'number' || typeof regionalData === 'string') {
        const regionalQty = parseInt(regionalData, 10);
        if (!isNaN(regionalQty)) effective = Math.min(effective, Math.max(0, regionalQty));
      }

      // Novo formato estruturado: { total, regions: { sp, sjc, rj } }
      const code = window.JVRegionalStock.getState()?.code;
      if (code && regionalData && typeof regionalData === 'object' && regionalData.regions && typeof regionalData.regions === 'object') {
        const regionalQty = parseInt(regionalData.regions[String(code).toLowerCase()], 10);
        if (!isNaN(regionalQty)) effective = Math.min(effective, Math.max(0, regionalQty));
      }

      // Compatibilidade com formato legado por regiao no root.
      if (code && regionalData && typeof regionalData === 'object' && Object.prototype.hasOwnProperty.call(regionalData, String(code).toLowerCase())) {
        const regionalQty = parseInt(regionalData[String(code).toLowerCase()], 10);
        if (!isNaN(regionalQty)) effective = Math.min(effective, Math.max(0, regionalQty));
      }
    }

    return effective;
  };

  /**
   * Igual ao product-price: o fetch da variante inclui HTML re-renderizado com metacampos corretos.
   * Copiamos script JSON, data-* da raiz, barra de progresso, limites por linha e preço do botão.
   * @param {HTMLElement} root
   * @param {Event & { detail?: { data?: { html?: Document; newProduct?: { id?: string }; productId?: string } } }} } event
   * @returns {boolean}
   */
  const syncKitFromResponseHtml = (root, event) => {
    const doc = event.detail?.data?.html;
    if (!doc || typeof doc.querySelector !== 'function') return false;

    if (event.detail.data.newProduct?.id) {
      root.dataset.kitRulesProductId = event.detail.data.newProduct.id;
      root.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId && root.dataset.kitRulesProductId) {
      if (String(event.target.dataset.productId) !== String(root.dataset.kitRulesProductId)) {
        return false;
      }
    }

    const blockId = root.dataset.jvKitBlockId;
    /** @type {Element | null} */
    let newKit = null;
    if (blockId) {
      try {
        newKit = doc.querySelector(`[data-jv-kit-builder][data-jv-kit-block-id="${CSS.escape(blockId)}"]`);
      } catch (_) {
        newKit = null;
      }
    }
    if (!newKit && root.dataset.kitRulesProductId) {
      try {
        newKit = doc.querySelector(
          `[data-jv-kit-builder][data-kit-rules-product-id="${CSS.escape(root.dataset.kitRulesProductId)}"]`
        );
      } catch (_) {
        newKit = null;
      }
    }
    if (!newKit) return false;

    const attrs = [
      'minItems',
      'maxItems',
      'extraRequiredQty',
      'kitVariantId',
      'kitRulesProductId',
      'kitRulesSource',
      'extraRequiredMessage',
      'extraProgressTargetMode',
      'kitProgressLabelTemplate',
      'productId'
    ];
    for (const key of attrs) {
      if (newKit.dataset[key] !== undefined && newKit.dataset[key] !== '') {
        root.dataset[key] = newKit.dataset[key];
      }
    }

    const newScript = newKit.querySelector('script[data-jv-kit-rules]');
    const oldScript = root.querySelector('script[data-jv-kit-rules]');
    if (newScript && oldScript) {
      oldScript.textContent = newScript.textContent;
    }

    const newProgress = newKit.querySelector('.jv-kit-builder__progress');
    const oldProgress = root.querySelector('.jv-kit-builder__progress');
    if (newProgress && oldProgress) {
      oldProgress.innerHTML = newProgress.innerHTML;
    }

    const oldByVid = new Map();
    for (const el of root.querySelectorAll('[data-kit-item]')) {
      if (el.dataset.variantId) oldByVid.set(String(el.dataset.variantId), el);
    }
    newKit.querySelectorAll('[data-kit-item]').forEach((ni) => {
      const oi = oldByVid.get(String(ni.dataset.variantId));
      if (!oi) return;
      ['maxQty', 'storeQty', 'regionalStock', 'available', 'price'].forEach((k) => {
        if (ni.dataset[k] !== undefined) oi.dataset[k] = ni.dataset[k];
      });
      oi.className = ni.className;
    });

    const newPrice = newKit.querySelector('.jv-kit-builder__add-price');
    const oldPrice = root.querySelector('.jv-kit-builder__add-price');
    if (newPrice && oldPrice) {
      oldPrice.textContent = newPrice.textContent;
    }

    return true;
  };

  /** @type {boolean} */
  let variantUpdateHookInstalled = false;

  const initBuilder = (root) => {
    let rulesData = null;
    const parseRulesFromDom = () => {
      const rulesScript = root.querySelector('script[data-jv-kit-rules]');
      if (!rulesScript?.textContent) {
        rulesData = null;
        return;
      }
      try {
        rulesData = JSON.parse(rulesScript.textContent);
      } catch (_) {
        rulesData = null;
      }
    };

    parseRulesFromDom();

    let minItems = parseInt(root.dataset.minItems, 10) || 0;
    let maxItems = parseInt(root.dataset.maxItems, 10) || 14;
    let extraRequiredQty = Math.max(0, parseInt(root.dataset.extraRequiredQty, 10) || 0);
    const extraRequiredMessage = root.dataset.extraRequiredMessage || 'Escolha mais [extra] unidades sem custo adicional.';
    const extraProgressTargetMode = root.dataset.extraProgressTargetMode || 'always_total';
    const addBtn = root.querySelector('[data-kit-add-btn]');
    const summary = root.querySelector('[data-kit-summary]');
    /** @type {HTMLElement | null | undefined} */
    let progressCount;
    /** @type {HTMLElement | null | undefined} */
    let progressTarget;
    /** @type {HTMLElement | null | undefined} */
    let progressFill;
    /** @type {HTMLElement | null | undefined} */
    let progressLabel;
    /** @type {HTMLElement | null | undefined} */
    let progressBadge;
    let defaultProgressLabel = '';

    const bindProgressRefs = () => {
      progressCount = root.querySelector('[data-kit-progress-count]');
      progressTarget = root.querySelector('[data-kit-progress-target]');
      progressFill = root.querySelector('[data-kit-progress-fill]');
      progressLabel = root.querySelector('[data-kit-progress-label]');
      progressBadge = root.querySelector('[data-kit-progress-badge]');
      defaultProgressLabel = progressLabel?.dataset.defaultLabel || '';
    };

    bindProgressRefs();
    const moneyFormat = root.dataset.moneyFormat || '';

    const getItemEls = () => Array.from(root.querySelectorAll('[data-kit-item]'));

    const getKitVariantId = () => root.dataset.kitVariantId || '';

    const formatMoney = (cents) => {
      const n = Number(cents);
      if (typeof window.Shopify !== 'undefined' && typeof window.Shopify.formatMoney === 'function') {
        return window.Shopify.formatMoney(n, moneyFormat || undefined);
      }
      return (n / 100).toFixed(2);
    };

    if (!getItemEls().length || !addBtn) return;

    const state = new Map();
    let cartItems = null;

    const fetchCartState = async () => {
      try {
        const res = await fetch('/cart.js', { credentials: 'same-origin' });
        if (!res.ok) return;
        const data = await res.json();
        cartItems = data.items || [];
      } catch (_) {
        cartItems = [];
      }
    };

    const getCartQtyForVariant = (variantId) => {
      if (!cartItems) return 0;
      let total = 0;
      for (const item of cartItems) {
        if (String(item.variant_id) === String(variantId)) total += item.quantity;
      }
      return total;
    };

    const getTotalSelected = () => {
      let sum = 0;
      for (const qty of state.values()) sum += qty;
      return sum;
    };

    const ensureStateKeysForItems = () => {
      for (const el of getItemEls()) {
        const vid = el.dataset.variantId;
        if (vid && !state.has(vid)) state.set(vid, 0);
      }
    };

    const updateUI = () => {
      ensureStateKeysForItems();
      const itemEls = getItemEls();
      const total = getTotalSelected();
      const triggerQty = maxItems;
      const hasExtraRule = extraRequiredQty > 0;
      const effectiveMaxItems = hasExtraRule ? Math.max(maxItems, triggerQty + extraRequiredQty) : maxItems;
      const shouldShowExtraTarget =
        hasExtraRule && (extraProgressTargetMode === 'always_total' || total >= triggerQty);
      const displayTarget = shouldShowExtraTarget ? effectiveMaxItems : maxItems;
      const pendingExtra =
        hasExtraRule && total >= triggerQty && total < (triggerQty + extraRequiredQty)
          ? triggerQty + extraRequiredQty - total
          : 0;
      const pctBase = Math.max(1, displayTarget);
      const pct = Math.min((total / pctBase) * 100, 100);

      if (progressCount) progressCount.textContent = total;
      if (progressTarget) progressTarget.textContent = displayTarget;
      if (progressFill) progressFill.style.width = pct + '%';
      if (progressLabel) {
        if (pendingExtra > 0) {
          progressLabel.textContent = extraRequiredMessage.replace(/\[extra\]/g, String(pendingExtra));
        } else {
          progressLabel.textContent = defaultProgressLabel;
        }
      }
      if (progressBadge) {
        progressBadge.hidden = pendingExtra <= 0 && minItems <= 0;
      }

      addBtn.disabled = total < minItems || pendingExtra > 0 || !getKitVariantId();

      for (const el of itemEls) {
        const variantId = el.dataset.variantId;
        const qty = state.get(variantId) || 0;
        const qtyEl = el.querySelector('[data-kit-qty]');
        const minusBtn = el.querySelector('[data-kit-minus]');
        const plusBtn = el.querySelector('[data-kit-plus]');

        if (qtyEl) qtyEl.textContent = qty;
        if (minusBtn) minusBtn.disabled = qty <= 0;

        if (plusBtn) {
          const effectiveMax = getEffectiveMax(el);
          const cartQty = getCartQtyForVariant(variantId);
          const reachLimit = (qty + cartQty) >= effectiveMax;
          const reachTotal = total >= effectiveMaxItems;
          plusBtn.disabled = reachLimit || reachTotal;
        }

        el.classList.toggle('jv-kit-builder-item--active', qty > 0);
      }

      if (summary) {
        if (total > 0) {
          const names = [];
          for (const el of itemEls) {
            const qty = state.get(el.dataset.variantId) || 0;
            if (qty > 0) names.push(`${qty}x ${el.dataset.productTitle}`);
          }
          summary.textContent = names.join(' · ');
        } else {
          summary.textContent = '';
        }
      }
    };

    const applyKitVariantRules = (variantId, options = {}) => {
      const skipReset = options.skipReset === true;
      parseRulesFromDom();
      if (!rulesData || variantId == null || variantId === '') return;
      if (String(rulesData.productId) !== String(root.dataset.kitRulesProductId || '')) return;

      const cfg = rulesData.byVariant && rulesData.byVariant[String(variantId)];
      const fallback = rulesData.fallback || {};
      const pack = cfg || fallback;

      const pMax = parseInt(String(pack.maxItems), 10);
      const pMin = parseInt(String(pack.minItems), 10);
      const pExtra = parseInt(String(pack.extraRequired), 10);

      if (!isNaN(pMax) && pMax > 0) {
        maxItems = pMax;
        minItems = !isNaN(pMin) && pMin > 0 ? pMin : pMax;
      }
      extraRequiredQty = !isNaN(pExtra) && pExtra >= 0 ? pExtra : 0;

      root.dataset.minItems = String(minItems);
      root.dataset.maxItems = String(maxItems);
      root.dataset.extraRequiredQty = String(extraRequiredQty);

      const tmpl = root.dataset.kitProgressLabelTemplate || 'Escolha __MAX__ opções';
      defaultProgressLabel = tmpl.replace(/__MAX__/g, String(maxItems));
      if (progressLabel) progressLabel.setAttribute('data-default-label', defaultProgressLabel);

      const list = pack.maxQtyList;
      if (Array.isArray(list) && list.length) {
        const itemArr = getItemEls();
        itemArr.forEach((el, i) => {
          const v = list[i];
          const parsed = parseInt(String(v), 10);
          if (!isNaN(parsed)) {
            el.dataset.maxQty = String(parsed > 0 ? parsed : 999);
          }
        });
      }

      if (!skipReset) {
        state.clear();
        for (const el of getItemEls()) state.set(el.dataset.variantId, 0);
      }
      updateUI();
    };

    const handlePlus = (el) => {
      const variantId = el.dataset.variantId;
      const current = state.get(variantId) || 0;
      state.set(variantId, current + 1);
      updateUI();
    };

    const handleMinus = (el) => {
      const variantId = el.dataset.variantId;
      const current = state.get(variantId) || 0;
      if (current <= 0) return;
      state.set(variantId, current - 1);
      updateUI();
    };

    const buildProperties = () => {
      const props = {};
      let idx = 1;
      const dataPairs = [];

      for (const el of getItemEls()) {
        const qty = state.get(el.dataset.variantId) || 0;
        if (qty <= 0) continue;
        props[`Prato ${idx}`] = `${qty}x ${el.dataset.productTitle}`;
        dataPairs.push(`${el.dataset.variantId}:${qty}`);
        idx++;
      }

      props['_items'] = dataPairs.join(',');
      return props;
    };

    const getCartSectionIds = () => {
      const ids = [];
      for (const el of document.querySelectorAll('cart-items-component')) {
        if (el.dataset.sectionId) ids.push(el.dataset.sectionId);
      }
      return ids;
    };

    const dispatchCartEvent = (itemCount, sections) => {
      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: {},
          sourceId: 'jv-kit-builder',
          data: {
            source: 'product-form-component',
            itemCount,
            sections: sections || {}
          }
        }
      }));
    };

    const addToCart = async () => {
      const kitVariantId = getKitVariantId();
      if (!kitVariantId) return;
      addBtn.disabled = true;
      const labelEl = addBtn.querySelector('.jv-kit-builder__add-label');
      const priceEl = addBtn.querySelector('.jv-kit-builder__add-price');
      const sepEl = addBtn.querySelector('.jv-kit-builder__add-sep');
      const prevLabel = labelEl ? labelEl.textContent : '';
      if (labelEl) labelEl.textContent = 'Adicionando...';
      if (priceEl) priceEl.hidden = true;
      if (sepEl) sepEl.hidden = true;

      try {
        const properties = buildProperties();
        const sectionIds = getCartSectionIds();

        const body = {
          id: parseInt(kitVariantId, 10),
          quantity: 1,
          properties
        };
        if (sectionIds.length) body.sections = sectionIds.join(',');

        const res = await fetch(
          (window.Theme?.routes?.cart_add_url) || '/cart/add.js',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body)
          }
        );

        const data = await res.json();

        if (data.status) {
          throw new Error(data.description || data.message || 'Erro ao adicionar ao carrinho');
        }

        if (labelEl) labelEl.textContent = 'Adicionado!';
        state.clear();

        dispatchCartEvent(1, data.sections);

        setTimeout(() => {
          if (labelEl) labelEl.textContent = prevLabel;
          if (priceEl) priceEl.hidden = false;
          if (sepEl) sepEl.hidden = false;
          fetchCartState().then(updateUI);
        }, 1500);
      } catch (err) {
        if (labelEl) labelEl.textContent = err.message || 'Erro - Tente novamente';
        setTimeout(() => {
          if (labelEl) labelEl.textContent = prevLabel;
          if (priceEl) priceEl.hidden = false;
          if (sepEl) sepEl.hidden = false;
          addBtn.disabled = false;
        }, 3000);
      }
    };

    for (const el of getItemEls()) {
      state.set(el.dataset.variantId, 0);

      const plusBtn = el.querySelector('[data-kit-plus]');
      const minusBtn = el.querySelector('[data-kit-minus]');

      if (plusBtn) plusBtn.addEventListener('click', () => handlePlus(el));
      if (minusBtn) minusBtn.addEventListener('click', () => handleMinus(el));
    }

    addBtn.addEventListener('click', addToCart);

    root.__jvKitHandleVariantUpdate = (e) => {
      if (!root.isConnected) return;

      if (e.detail?.data?.newProduct?.id) {
        root.dataset.kitRulesProductId = e.detail.data.newProduct.id;
        root.dataset.productId = e.detail.data.newProduct.id;
      } else if (e.target instanceof HTMLElement && e.target.dataset.productId && root.dataset.kitRulesProductId) {
        if (String(e.target.dataset.productId) !== String(root.dataset.kitRulesProductId)) {
          return;
        }
      }

      const syncedFromHtml = syncKitFromResponseHtml(root, e);
      bindProgressRefs();
      parseRulesFromDom();

      minItems = parseInt(root.dataset.minItems, 10) || 0;
      maxItems = parseInt(root.dataset.maxItems, 10) || 14;
      extraRequiredQty = Math.max(0, parseInt(root.dataset.extraRequiredQty, 10) || 0);

      let resource = e.detail?.resource;
      let variantKey = resource?.id != null ? String(resource.id) : '';

      if (!variantKey) {
        variantKey = root.dataset.kitVariantId || '';
      }
      if (!variantKey && rulesData) {
        const fromForm = document.querySelector('product-form-component input[name="id"]')?.value;
        const fromUrl = new URLSearchParams(window.location.search).get('variant');
        const tryId = fromForm || fromUrl;
        if (tryId && rulesData.byVariant && Object.prototype.hasOwnProperty.call(rulesData.byVariant, String(tryId))) {
          variantKey = String(tryId);
          resource = resource || { id: variantKey, price: null };
        }
      }

      if (!variantKey) return;

      if (syncedFromHtml) {
        root.dataset.kitVariantId = variantKey;
        const priceEl = root.querySelector('.jv-kit-builder__add-price');
        if (priceEl != null && resource?.price != null) {
          priceEl.textContent = formatMoney(resource.price);
        }
        state.clear();
        for (const el of getItemEls()) state.set(el.dataset.variantId, 0);
        updateUI();
        return;
      }

      const ourPid = root.dataset.kitRulesProductId;
      const evtPid = e.detail?.data?.productId;
      const variantInRules =
        rulesData &&
        rulesData.byVariant &&
        Object.prototype.hasOwnProperty.call(rulesData.byVariant, variantKey);
      const productMatches = ourPid && String(evtPid) === String(ourPid);

      if (!variantInRules && (!ourPid || !productMatches)) return;

      root.dataset.kitVariantId = variantKey;
      const priceEl = root.querySelector('.jv-kit-builder__add-price');
      if (priceEl != null && resource?.price != null) {
        priceEl.textContent = formatMoney(resource.price);
      }
      if (rulesData) {
        applyKitVariantRules(variantKey);
      } else {
        updateUI();
      }
    };

    document.addEventListener('cart:update', (e) => {
      if (e.detail?.sourceId === 'jv-kit-builder') return;
      fetchCartState().then(updateUI);
    });
    document.addEventListener('jv:region-changed', () => {
      fetchCartState().then(updateUI);
    });

    if (rulesData && root.dataset.kitVariantId) {
      applyKitVariantRules(root.dataset.kitVariantId, { skipReset: true });
    }

    fetchCartState().then(updateUI);
  };

  const init = () => {
    for (const root of document.querySelectorAll('[data-jv-kit-builder]')) {
      if (root.dataset.kitBuilderInitialized) continue;
      root.dataset.kitBuilderInitialized = 'true';
      initBuilder(root);
    }
  };

  const onGlobalVariantUpdate = (e) => {
    init();
    for (const root of document.querySelectorAll('[data-jv-kit-builder]')) {
      if (!root.isConnected || !root.dataset.kitBuilderInitialized) continue;
      const fn = root.__jvKitHandleVariantUpdate;
      if (typeof fn === 'function') fn(e);
    }
  };

  if (!variantUpdateHookInstalled) {
    variantUpdateHookInstalled = true;
    document.addEventListener('variant:update', onGlobalVariantUpdate);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', init);
    document.addEventListener('shopify:block:select', init);
  }
})();
